import { supabase } from '../../../shared/lib/supabaseClient';
import { callStudentRpc } from '../../auth/services/studentAuthService';

// Students aren't Supabase Auth users, so their own grades, scores and attendance come through the
// student_* database functions, which find the student from their session token. Subjects and
// teachers are public, so those are still read directly.

// The signed-in student's own row, plus `is_secretary`.
export async function getOwnStudent() {
  const rows = await callStudentRpc('student_get_self');
  return rows[0] ?? null;
}

// Changes the student's own username and, when `newPassword` is given, password (student_update_login
// in schema.sql). Needs their current password either way.
export async function updateOwnLogin({ currentPassword, username, newPassword }) {
  await callStudentRpc('student_update_login', {
    p_current_password: currentPassword,
    p_username: username,
    p_new_password: newPassword || null,
  });
}

// The subjects taught to the student's grade level and section.
export async function getSubjectsForStudent(student) {
  if (!student.grade_level || !student.section) return [];

  const { data, error } = await supabase
    .from('subjects')
    .select('id, name, category, teachers(firstname, lastname), subject_sections!inner(section)')
    .eq('grade_level', student.grade_level)
    .eq('subject_sections.section', student.section)
    .order('name', { ascending: true });
  if (error) throw error;

  return data.map((subject) => ({
    subjectId: subject.id,
    name: subject.name,
    category: subject.category,
    teacher: subject.teachers ? `${subject.teachers.firstname} ${subject.teachers.lastname}` : 'Unassigned',
  }));
}

// Term grades for every subject and term, keyed by `${subjectId}|${term}`. A subject/term with no
// grade yet has no entry (a term grade needs a score in every graded component).
async function getTermGrades() {
  const data = await callStudentRpc('student_get_grades');
  return Object.fromEntries(
    data.filter((row) => row.term_grade != null).map((row) => [`${row.subject_id}|${row.term}`, row.term_grade])
  );
}

// Attendance counts per subject and term: [{ subjectId, term, present, late, absent, excused }].
async function getAttendanceSummary() {
  const data = await callStudentRpc('student_get_attendance_summary');
  return data.map((row) => ({
    subjectId: row.subject_id,
    term: row.term,
    present: Number(row.present),
    late: Number(row.late),
    absent: Number(row.absent),
    excused: Number(row.excused),
  }));
}

// Teachers' "addressed" marks on the student (attention_reviews in schema.sql):
// [{ reason, subjectId, term, grade, absent, subjectIds }]. `subjectIds` are the reviewer's subjects,
// which an absenteeism mark covers.
async function getAttentionReviews() {
  const data = await callStudentRpc('student_get_attention_reviews');
  return data.map((row) => ({
    reason: row.reason,
    subjectId: row.subject_id,
    term: row.term,
    grade: row.grade,
    absent: row.absent,
    subjectIds: row.subject_ids ?? [],
  }));
}

// Used by the student dashboard to show the signed-in student's own record. Attendance failing to
// load is reported separately so it can't take the grades down with it; without the teachers' marks
// every alert is just shown.
export async function getOwnStudentRecord() {
  const student = await getOwnStudent();
  if (!student) return null;

  const [subjects, grades, attendanceResult, attentionReviews] = await Promise.all([
    getSubjectsForStudent(student),
    getTermGrades(),
    getAttendanceSummary()
      .then((rows) => ({ rows, error: '' }))
      .catch((err) => ({ rows: [], error: err.message || 'Failed to load attendance' })),
    getAttentionReviews().catch(() => []),
  ]);
  return {
    student,
    subjects,
    grades,
    attendance: attendanceResult.rows,
    attendanceError: attendanceResult.error,
    attentionReviews,
  };
}

// The student's individual attendance records for one term, newest first, for the by-date view:
// [{ date, subjectId, status }]. Fetched per term (not all at once) to stay under the API's row limit.
export async function getOwnAttendanceRecords(term) {
  const data = await callStudentRpc('student_get_attendance', { p_term: term });
  return data.map((row) => ({ date: row.attendance_date, subjectId: row.subject_id, status: row.status }));
}

// Used by the student dashboard's per-subject page. Returns null when the subject isn't one the
// student takes. `grades` is keyed by term; `assessments` are the student's scored items.
export async function getOwnSubjectRecord(subjectId) {
  const student = await getOwnStudent();
  if (!student) return null;

  const subject = (await getSubjectsForStudent(student)).find((s) => s.subjectId === subjectId);
  if (!subject) return null;

  const [grades, scores, attendance] = await Promise.all([
    callStudentRpc('student_get_grades', { p_subject_id: subjectId }),
    callStudentRpc('student_get_scores', { p_subject_id: subjectId }),
    callStudentRpc('student_get_attendance', { p_subject_id: subjectId }),
  ]);

  const assessments = scores
    .map((row) => ({
      id: row.assessment_id,
      component: row.component,
      term: row.term,
      title: row.title,
      date: row.assessment_date,
      maxScore: Number(row.max_score),
      score: Number(row.score),
      comment: row.comment ?? '',
    }))
    .sort((a, b) => a.term - b.term || String(a.date ?? '').localeCompare(String(b.date ?? '')) || a.title.localeCompare(b.title));

  return {
    ...subject,
    grades: Object.fromEntries(grades.map((row) => [row.term, row])),
    assessments,
    attendance: attendance.map(({ attendance_date, term, status }) => ({ attendance_date, term, status })),
  };
}
